import { AnimationStyles, Checkbox, mergeStyles, mergeStyleSets, Text, TextField, useTheme } from '@fluentui/react';
import { ChevronLeftIcon, ErrorIcon, InfoIcon, StatusCircleCheckmarkIcon, WarningIcon } from '@fluentui/react-icons-mdl2';
import { EValidationSeverity, IValidationMessage } from 'components/controls/ValidationView/ValidationView.type';
import React, { useMemo } from 'react';

const visibleStyle = { opacity: '1', transition: 'opacity 1.4s', maxHeight: 'none' }
const invisibleStyle = { opacity: '0', transition: 'opacity 0s', maxHeight: '0', display: "none" }

interface IProps {
    data: IValidationMessage;
    onChange?: (data: IValidationMessage) => void
}

export default function ValidationItem(props: IProps) {
    const [isCollapsed, setIsCollapsed] = React.useState(true);

    const theme = useTheme();

    const canCollapse = props.data.description || props.data.inner || props.data.onRenderDescription;

    // todo useMemo
    const collapsibleHeaderClass = useMemo(() => mergeStyles({
        backgroundColor: theme.semanticColors.menuBackground,
        color: theme.semanticColors.bodyText,
        // background:theme.palette.themePrimary,
        fontSize: theme.fonts.mediumPlus.fontSize,
        cursor: canCollapse ? 'pointer' : undefined,
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'pre-line',
        minHeight: '36px',
        // borderBottom: '1px solid ' + theme.schemes?.soft?.semanticColors.menuDivider,
        ':hover': {
            backgroundColor: theme.semanticColors.menuItemBackgroundHovered
        }
    }), [theme]);

    const contentStyles = useMemo(() => {
        return mergeStyleSets({
            warnIcon: { fontSize: '16pt', color: 'darkorange' },
            infoIcon: { fontSize: '16pt', color: 'blue' },
            errorIcon: { fontSize: '16pt', color: 'red' },
            successIcon: { fontSize: '16pt', color: 'darkgreen' },
        });
    }, [theme]);

    const contentStyles2 = mergeStyles(AnimationStyles.fadeIn500, {
        padding: '0px 18px',
        overflow: 'hidden',
        transition: 'max-height 0.1s ease-out',
        maxHeight: '0'
    });

    const headerOnClick = () => {
        // not description to expand to then nop.
        if (!canCollapse)
            return;

        setIsCollapsed(!isCollapsed);
    }

    function onRenderHeader() {
        let prompt = <></>;

        switch (props.data.type) {
            case EValidationSeverity.promptBoolean:
                prompt = <div style={{ fontWeight: isCollapsed ? 300 : 450, padding: '4px' }}>
                    <Checkbox
                        label={props.data.title}
                        onChange={(event, checked) => {
                            const t = { ...props.data, value: checked ? "yes" : "no" };

                            // change value
                            props.onChange?.(t);

                            // raise any subscriber with new data
                            t.onChange?.(t);
                        }}
                    />
                </div>
                break;

            case EValidationSeverity.promptNote:
                prompt = <div style={{ fontWeight: isCollapsed ? 300 : 450, padding: '4px' }}>
                    <TextField
                        label={props.data.title}
                        styles={{ root: { width: '100%' } }}
                        multiline
                        onChange={(event, newValue) => {
                            const t = { ...props.data, value: newValue ?? null };

                            // change value
                            props.onChange?.(t);

                            // raise any subscriber with new data
                            t.onChange?.(t);
                        }}
                    />
                </div>
                break;

            default:
                prompt = <div style={{ marginLeft: '5px', fontWeight: isCollapsed ? 300 : 450, padding: '4px' }}>
                    {props.data.title}
                </div>
                break;
        }

        return <div
            className={collapsibleHeaderClass}
            onClick={() => headerOnClick()}
            onKeyDown={(event) => {

                // http://www.mit.edu/~rjc/aria/expandingSections.html#
                if (event.code === "Space" || event.code === "Enter" || event.code === "ArrowRight") {
                    event.preventDefault();
                    headerOnClick();
                }
            }}
            tabIndex={0}
        >

            <div style={{ marginLeft: '12px', transition: '180ms ease all', }}>
                {props.data.type && renderIcon(props.data)}
            </div>

            {prompt}

            {canCollapse &&
                <div style={{
                    marginLeft: 'auto',
                    marginRight: '15px',
                    transition: '180ms ease all',
                    transform: !isCollapsed ? 'rotate(-90deg)' : '',
                    float: 'right',
                }}>
                    <ChevronLeftIcon style={{ fontSize: 'x-small' }} />
                </div>
            }

        </div>;
    }

    function onRenderDetails() {

        return <div className={contentStyles2} style={isCollapsed ? invisibleStyle : visibleStyle} >
            <div>
                {props.data.description &&
                    <div style={{ padding: '8px' }}>
                        <Text variant='small' styles={{ root: { whiteSpace: 'pre-line' } }}>
                            {props.data.description}
                        </Text>
                    </div>
                }
                {props.data.onRenderDescription &&
                    props.data.onRenderDescription()
                }
                {onRenderDetailsInnerValidations()}
            </div>
        </div>;
    }

    function onRenderDetailsInnerValidations() {
        return <div style={{ marginTop: '8px' }}>
            {props.data.inner && props.data.inner.length > 0 &&
                props.data.inner.map((data, dataIx) => {
                    return <ValidationItem key={dataIx} data={data} />;
                })
            }
        </div>;
    }

    const renderIcon = (validation: IValidationMessage) => {
        switch (validation.type) {
            case EValidationSeverity.warn:
                return <WarningIcon className={contentStyles.warnIcon} />;

            case EValidationSeverity.info:
                return <InfoIcon className={contentStyles.infoIcon} />;

            case EValidationSeverity.error:
                return <ErrorIcon className={contentStyles.errorIcon} />;

            case EValidationSeverity.success:
                return <StatusCircleCheckmarkIcon className={contentStyles.successIcon} />;

            case EValidationSeverity.promptBoolean:
                return <></>;
            // return <EditIcon className={contentStyles.infoIcon} />

            default:
                return <></>;
        }
    };

    // if (props.data.severity === EValidationSeverity.promptBoolean)
    //     return <div style={{ width: '100%', marginBottom: '5px', border: '2px solid rgba(180,180,180,0.1)' }}>
    //         <Stack style={{ padding: '10px' }} tokens={{ childrenGap: '8px' }} >
    //             <Checkbox
    //                 label={props.data.title}
    //                 onChange={(event, checked) => {
    //                     const t = { ...props.data, value: checked ? "yes" : "no" };

    //                     // change value
    //                     props.onChange?.(t);

    //                     // raise any subscriber with new data
    //                     t.onChange?.(t);
    //                 }}
    //             />
    //             <Text variant='xSmall' styles={{ root: { whiteSpace: 'pre-line' } }}>
    //                 {props.data.description}
    //             </Text>
    //         </Stack>
    //     </div>;

    return <div style={{ width: '100%', marginBottom: '5px', border: '2px solid rgba(180,180,180,0.1)', paddingBottom: isCollapsed ? '0' : '6px' }}>
        {onRenderHeader()}
        {onRenderDetails()}
    </div>;
}
